const BRAND_NAME = "ClearBadge";
export const BRAND_POSITIONING =
  "ClearBadge verifies professional licenses so consumers can instantly confirm who they're dealing with.";
const SITE_URL = "https://www.clearbadge.org";

export const HOME_FAQ_ITEMS = [
  {
    question: "What does ClearBadge verify?",
    answer:
      "ClearBadge verifies professional licenses and identity signals using public licensing data and verification checks.",
  },
  {
    question: "Is ClearBadge free for consumers?",
    answer:
      "Yes. Consumers can search by phone, email, or name and state to view verification results for free.",
  },
  {
    question: "How do I verify a professional?",
    answer:
      "Enter a phone number, email address, or name and state. ClearBadge returns verification status and license details.",
  },
  {
    question: "What does 'Not Verified' mean?",
    answer:
      "Not Verified means the professional has not completed ClearBadge verification. It does not imply lack of licensure.",
  },
  {
    question: "Which industries and states are covered?",
    answer:
      "ClearBadge focuses on licensed professions across the US. Coverage varies by industry and state based on data availability.",
  },
];

const DEFAULT_IMAGE = `${SITE_URL}/og-image.webp`;
const DEFAULT_IMAGE_TYPE = "image/webp";
const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 630;
const DEFAULT_TITLE = `${BRAND_NAME} | Professional License Verification`;
const NOT_FOUND_TITLE = `404 | Page Not Found | ${BRAND_NAME}`;
const NOT_FOUND_DESCRIPTION = "The requested ClearBadge page could not be found.";

const SEO_ROUTES = [
  {
    pattern: /^\/$/,
    title: `Professional License Verification | ${BRAND_NAME}`,
    description: `${BRAND_POSITIONING} Search by phone, email, or name and state.`,
    indexable: true,
    includeFaq: true,
    includeSoftware: true,
    ogType: "website",
  },
  {
    pattern: /^\/find-a-pro\/?$/,
    title: `Find a Verified Professional | ${BRAND_NAME}`,
    description:
      "Find verified professionals by industry and location. Filter by license status and verification level.",
    indexable: true,
    includeSoftware: true,
    ogType: "website",
  },
  {
    pattern: /^\/pricing\/?$/,
    title: `Pricing | ${BRAND_NAME}`,
    description:
      "Plans for professionals to verify credentials, publish a ClearBadge profile, and build consumer trust.",
    indexable: true,
    includeSoftware: true,
    ogType: "website",
  },
  {
    pattern: /^\/trial-invite\/?$/,
    title: `7-Day Pro Trial Invite | ${BRAND_NAME}`,
    description:
      "Start your invited 7-day free trial of ClearBadge Pro and compare Free vs Pro before you choose a plan.",
    indexable: true,
    includeSoftware: true,
    ogType: "website",
  },
  {
    pattern: /^\/elite-trial-invite\/?$/,
    title: `7-Day Elite Trial Invite | ${BRAND_NAME}`,
    description:
      "Start your invited 7-day free trial of ClearBadge Elite and compare Free vs Elite before you choose a plan.",
    indexable: true,
    includeSoftware: true,
    ogType: "website",
  },
  {
    pattern: /^\/about\/?$/,
    title: `About ClearBadge | Professional License Verification Platform`,
    description:
      "Learn how ClearBadge helps consumers and licensed professionals reduce AI fraud risk with fast professional license verification.",
    keywords:
      "about clearbadge, professional license verification platform, verify licensed professional, AI fraud prevention, credential verification",
    image: `${SITE_URL}/about-1.jpg`,
    imageType: "image/jpeg",
    imageWidth: 1920,
    imageHeight: 1078,
    imageAlt: "ClearBadge mission and professional license verification",
    indexable: true,
    webPageType: "AboutPage",
    includeService: true,
    includeSoftware: true,
    ogType: "website",
  },
  {
    pattern: /^\/affiliate\/?$/,
    title: `Affiliate Program | ${BRAND_NAME}`,
    description:
      "Join the ClearBadge affiliate program and earn 25% recurring commission on paid customers you refer.",
    keywords:
      "clearbadge affiliate program, recurring affiliate commission, professional verification affiliate, SaaS affiliate program",
    indexable: true,
    includeSoftware: true,
    ogType: "website",
  },
  {
    pattern: /^\/privacy-policy\/?$/,
    title: `Privacy Policy | ${BRAND_NAME}`,
    description: "How ClearBadge collects, uses, and protects information.",
    indexable: true,
    ogType: "website",
  },
  {
    pattern: /^\/terms-of-service\/?$/,
    title: `Terms of Service | ${BRAND_NAME}`,
    description: "Terms for using ClearBadge services.",
    indexable: true,
    ogType: "website",
  },
  {
    pattern: /^\/delete-account\/?$/,
    title: `Delete Account | ${BRAND_NAME}`,
    description: "Instructions for requesting deletion of your ClearBadge account and associated data.",
    indexable: true,
    ogType: "website",
  },
  {
    pattern: /^\/verbal-consent-sms\/?$/,
    title: `Verbal Consent SMS | ${BRAND_NAME}`,
    description:
      "How ClearBadge captures verbal consent before sending transactional SMS messages for requested trust-session links.",
    indexable: true,
    ogType: "website",
  },
  {
    pattern: /^\/smstos\/?$/,
    title: `SMS Terms and Privacy | ${BRAND_NAME}`,
    description: "ClearBadge one-time transactional SMS terms, opt-out details, and privacy disclosures.",
    indexable: true,
    ogType: "website",
  },
  {
    pattern: /^\/p\/[^/]+\/?$/,
    title: `Verified Professional Profile | ${BRAND_NAME}`,
    description: BRAND_POSITIONING,
    indexable: true,
    includeSoftware: false,
    ogType: "profile",
  },
  {
    pattern: /^\/trust-session\/[^/]+\/?$/,
    title: `Trust Session Host Studio | ${BRAND_NAME}`,
    description: "Host a live, view-only ClearBadge trust session.",
    indexable: false,
  },
  {
    pattern: /^\/s\/[^/]+\/?$/,
    title: `Trust Session | ${BRAND_NAME}`,
    description: "Join a live, view-only ClearBadge trust session.",
    indexable: false,
  },
  {
    pattern: /^\/(admin|dashboard|licenses|leads|verification|my-info|plans)(\/|$)/,
    title: `${BRAND_NAME} | Member Portal`,
    description: "Access your ClearBadge account.",
    indexable: false,
  },
  {
    pattern: /^\/(login|lookup|find-pro|invite|claim)(\/|$)/,
    title: `${BRAND_NAME} | Access`,
    description: "Access ClearBadge services.",
    indexable: false,
  },
];

export const getSeoForPath = (pathname) => {
  const normalized = pathname || "/";
  const match = SEO_ROUTES.find((route) => route.pattern.test(normalized));
  if (!match) {
    return {
      title: NOT_FOUND_TITLE,
      description: NOT_FOUND_DESCRIPTION,
      keywords: "",
      image: DEFAULT_IMAGE,
      imageType: DEFAULT_IMAGE_TYPE,
      imageWidth: DEFAULT_IMAGE_WIDTH,
      imageHeight: DEFAULT_IMAGE_HEIGHT,
      imageAlt: `${BRAND_NAME} preview`,
      indexable: false,
      webPageType: "WebPage",
      includeService: false,
      includeFaq: false,
      includeSoftware: false,
      ogType: "website",
    };
  }
  return {
    title: match.title || DEFAULT_TITLE,
    description: match.description || BRAND_POSITIONING,
    keywords: match.keywords || "",
    image: match.image || DEFAULT_IMAGE,
    imageType: match.imageType || DEFAULT_IMAGE_TYPE,
    imageWidth: match.imageWidth || DEFAULT_IMAGE_WIDTH,
    imageHeight: match.imageHeight || DEFAULT_IMAGE_HEIGHT,
    imageAlt: match.imageAlt || `${BRAND_NAME} preview`,
    indexable: match.indexable !== false,
    webPageType: match.webPageType || "WebPage",
    includeService: !!match.includeService,
    includeFaq: !!match.includeFaq,
    includeSoftware: !!match.includeSoftware,
    ogType: match.ogType || "website",
  };
};

const titleCase = (value) =>
  String(value || "")
    .split(" ")
    .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : ""))
    .join(" ")
    .trim();

const BREADCRUMB_LABELS = {
  "find-a-pro": "Find a Pro",
  pricing: "Pricing",
  "trial-invite": "7-Day Trial Invite",
  "elite-trial-invite": "7-Day Elite Trial Invite",
  about: "About",
  affiliate: "Affiliate Program",
  "privacy-policy": "Privacy Policy",
  "terms-of-service": "Terms of Service",
  "delete-account": "Delete Account",
  "verbal-consent-sms": "Verbal Consent SMS",
  smstos: "SMS Terms and Privacy",
  p: "Verified Profile",
};

const buildBreadcrumbs = (pathname, baseUrl) => {
  const trimmed = pathname && pathname !== "/" ? pathname : "";
  if (!trimmed) return [];
  const segments = trimmed.split("/").filter(Boolean);
  if (segments.length === 0) return [];
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${baseUrl}/`,
    },
  ];
  if (segments[0] === "p") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: BREADCRUMB_LABELS.p,
      item: `${baseUrl}${trimmed}`,
    });
    return items;
  }
  let path = "";
  segments.forEach((segment) => {
    path += `/${segment}`;
    const label = BREADCRUMB_LABELS[segment] || segment.replace(/-/g, " ");
    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name: titleCase(label),
      item: `${baseUrl}${path}`,
    });
  });
  return items;
};

export const buildJsonLd = ({ baseUrl, canonical, meta, pathname }) => {
  const graph = [];

  graph.push({
    "@type": "Organization",
    "@id": `${baseUrl}/#organization`,
    name: BRAND_NAME,
    url: baseUrl,
    logo: `${baseUrl}/logo512.png`,
    image: `${baseUrl}/og-image.webp`,
    description: BRAND_POSITIONING,
  });

  graph.push({
    "@type": "WebSite",
    "@id": `${baseUrl}/#website`,
    name: BRAND_NAME,
    url: baseUrl,
    inLanguage: "en-US",
    description: "Verify professional licenses, find verified professionals, and build trust.",
  });

  graph.push({
    "@type": meta.webPageType || "WebPage",
    "@id": `${canonical}#webpage`,
    name: meta.title,
    url: canonical,
    description: meta.description,
    inLanguage: "en-US",
    image: meta.image,
    isPartOf: { "@id": `${baseUrl}/#website` },
    publisher: { "@id": `${baseUrl}/#organization` },
    about: { "@id": `${baseUrl}/#organization` },
  });

  const breadcrumbs = buildBreadcrumbs(pathname, baseUrl);
  if (breadcrumbs.length > 0) {
    graph.push({
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs,
    });
  }

  if (meta.includeSoftware) {
    graph.push({
      "@type": "SoftwareApplication",
      name: BRAND_NAME,
      applicationCategory: "SecurityApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      description: BRAND_POSITIONING,
      url: baseUrl,
    });
  }

  if (meta.includeService) {
    graph.push({
      "@type": "Service",
      name: "Professional License Verification",
      serviceType: "Professional credential and license verification",
      provider: { "@id": `${baseUrl}/#organization` },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: [
        {
          "@type": "Audience",
          audienceType: "Consumers",
        },
        {
          "@type": "Audience",
          audienceType: "Licensed Professionals",
        },
      ],
      description:
        "ClearBadge verifies professional licenses so consumers can instantly confirm who they are dealing with.",
      url: canonical,
    });
  }

  if (meta.includeFaq && HOME_FAQ_ITEMS.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: HOME_FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
};
